import {
  Button,
  Divider,
  FormControl,
  FormControlLabel,
  Radio,
  RadioGroup,
} from '@material-ui/core';
import React, { useState, useEffect } from 'react';
import moment from 'moment';
// @ts-ignore
import cx from 'classnames';
import { StartCollectionDatePicker } from './components/StartCollectionDatePicker';
import { EeModal } from 'components/EeModal/EeModal';
import { useSnackbar } from 'hooks/useSnackbar';

export interface SettingsProps {
  models: { company: any; settings: any; template: any };
  operations: any;
  editable: boolean;
  onSettingsEditable: (value: boolean) => void;
  settingsEditable: boolean;
}

export const Settings: React.FC<SettingsProps> = ({
  models,
  operations,
  editable,
  onSettingsEditable,
  settingsEditable,
}) => {
  const [startCollectionDate, setStartCollectionDate] = useState(
    new Date(models.settings.startDate),
  );
  const [cadence, setCadence] = useState(models.settings.period);
  const [modal, setModal] = useState({
    open: false,
    title: '',
    text: [] as any[],
  });
  const { addSnackbar } = useSnackbar();
  const closeModal = () => setModal({ ...modal, open: false });
  const [isCloseModalOpen, setIsCloseModalOpen] = useState(false);

  const handleSave = async () => {
    const startDate = moment(startCollectionDate).format('YYYY-MM-DD');
    const result = await operations.saveSettings(models.company, {
      startDate,
      period: cadence,
    });
    if (result.errorCode === 0) {
      const content = `Draft Saved: ${models.company.name} Settings were successfully saved.`;
      await operations.loadCompany(models.company.id);
      addSnackbar(content);
    } else {
      addSnackbar(`Failed to save ${models.company.name} Settings`);
    }
    closeModal();
    onSettingsEditable(false);
  };

  useEffect(() => {
    const startDate = new Date(models.settings.startDate);
    setStartCollectionDate(startDate);
    setCadence(models.settings.period);
  }, [models.settings.period, models.settings.startDate]);

  const getSettingsChanged = () => {
    const startDate = moment(startCollectionDate).format('YYYY-MM-DD');
    const periodChanged = models.settings.period !== cadence;
    const startDateChanged = models.settings.startDate !== startDate;
    return { periodChanged, startDateChanged };
  };

  const { periodChanged: pc, startDateChanged: sdc } = getSettingsChanged();
  const hasAnyChanges = pc || sdc;

  return (
    <div className="ml-4 flex-grow-1">
      <div className="w-100 p-4" style={{ backgroundColor: 'white' }}>
        <h3 className="d-flex flex-row justify-content-between pb-2">
          <span>Settings</span>
          {!settingsEditable && editable && (
            <div hidden={settingsEditable}>
              <Button color="primary" onClick={() => onSettingsEditable(true)}>
                Edit
              </Button>
            </div>
          )}
          {settingsEditable && editable && (
            <div hidden={!settingsEditable}>
              <Button
                color="primary"
                onClick={() => {
                  const {
                    periodChanged,
                    startDateChanged,
                  } = getSettingsChanged();
                  if (periodChanged || startDateChanged)
                    setIsCloseModalOpen(true);
                  else onSettingsEditable(false);
                }}
              >
                Cancel
              </Button>
              <Button
                disabled={!hasAnyChanges}
                color="primary"
                onClick={() => {
                  const {
                    periodChanged,
                    startDateChanged,
                  } = getSettingsChanged();
                  if (!periodChanged && !startDateChanged)
                    return onSettingsEditable(false);
                  let title = '';
                  let text = [] as any[];
                  if (periodChanged && startDateChanged) {
                    title = 'Save Settings - Start Date and Cadence';
                    text = [
                      'Depending on how the date was changed, columns will be added or removed from the beginning of the Data Table.',
                      'Depending on how the cadence was changed, columns will be changed to a monthly or quarterly frequency.',
                      'Any columns that currently have data and would be removed from this change will be saved; So you can revert and see the data again.',
                    ];
                  } else if (startDateChanged && !periodChanged) {
                    title = 'Save Settings - Start Date';
                    text = [
                      'Depending on how the date was changed, columns will be added or removed from the beginning of the Data Table.',
                      'Any columns that currently have data and would be removed from this change will be saved; So you can revert the start date and see the data again.',
                    ];
                  } else if (!startDateChanged && periodChanged) {
                    title = 'Save Settings - Cadence';
                    text = [
                      'Depending on how the cadence was changed, columns will be changed to a monthly or quarterly frequency.',
                      'Any columns that currently have data and would be removed from this change will be saved; So you can revert the cadence and see the data again.',
                    ];
                  }
                  setModal({ open: true, title, text });
                }}
              >
                Save
              </Button>
            </div>
          )}
        </h3>
        <Divider />

        <table className="mb-3">
          <tbody>
            <tr>
              <td className="font-weight-bold pb-4 pr-4">Current Template</td>
              <td className="pb-4">{models.template.name}</td>
            </tr>
            <tr>
              <td
                className="font-weight-bold pb-4 pr-4 pt-2"
                style={{ verticalAlign: 'top' }}
              >
                Start Collection date
              </td>
              <td
                className={cx('pb-4', { 'pt-2': !settingsEditable })}
                style={{ verticalAlign: 'top' }}
              >
                <span hidden={settingsEditable}>
                  Investment Start Date,{' '}
                  {moment(models.settings.startDate).format('DD MMM YYYY')}
                </span>
                {settingsEditable && (
                  <StartCollectionDatePicker
                    selectedDate={startCollectionDate}
                    onChange={({ date }) => setStartCollectionDate(date)}
                    // @ts-ignore
                    yearNavigationEnabled
                    format="DD MMM YYYY"
                    required
                  />
                )}
              </td>
            </tr>
            <tr>
              <td className="font-weight-bold pr-4">Collection Cadence</td>
              <td>
                <div hidden={settingsEditable}>
                  {cadence === 'M' ? 'Monthly' : 'Quarterly'}
                </div>
                <div hidden={!settingsEditable}>
                  <FormControl component="fieldset">
                    <RadioGroup
                      row
                      aria-label="cadence"
                      name="cadence"
                      value={cadence}
                      onChange={(e) => setCadence(e.target.value)}
                    >
                      <FormControlLabel
                        value="M"
                        control={<Radio />}
                        label="Monthly"
                        className="mr-4"
                      />
                      <FormControlLabel
                        value="Q"
                        control={<Radio />}
                        label="Quarterly"
                      />
                    </RadioGroup>
                  </FormControl>
                </div>
              </td>
            </tr>
          </tbody>
        </table>
        <EeModal
          open={isCloseModalOpen}
          title={`Cancel Settings`}
          submitTitle="Close Without Saving"
          onClose={() => setIsCloseModalOpen(false)}
          onSubmit={() => {
            setIsCloseModalOpen(false);
            setStartCollectionDate(models.settings.startDate);
            setCadence(models.settings.period);
            onSettingsEditable(false);
          }}
        >
          <p className="p-3 mb-0">
            Do you want to cancel settings without saving? Any changes made will
            not be saved.
          </p>
        </EeModal>
        <EeModal
          open={modal.open}
          onClose={closeModal}
          title={modal.title}
          submitTitle="Save Changes"
          onSubmit={handleSave}
        >
          <p>Are you sure you want to save these changes?</p>
          {modal.text.map((t, index) => (
            <p key={`modal-text-${index}`}>{t}</p>
          ))}
        </EeModal>
      </div>
    </div>
  );
};
